// Real Estate hub — Contacts tab (ticket 206, batch 206-2).
// The tab reads the unified contacts (contacts_v2), narrowed to the real-estate types; the
// legacy RE-hub store is retired (Damien 2026-10-03). Edit, delete, bulk and the full filter
// set live on the main Contacts page, one click away.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Loader2, Plus, Sparkles } from 'lucide-react';
import { api, isSpendApprovalError } from '../api';
import { useAuth } from '../AuthContext';
import { uc } from '../unifiedContactsApi';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from '../../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { errorMessage } from '../errors';
import PhoneInput from '../shared/PhoneInput';
import { primaryEmail } from '../shared/ContactSearchInput';
import { primaryPhone as primaryPhoneNumber } from '../shared/useTextRecipient';
import { formatPhone } from '../shared/phone';

// Same set the backend scores in Score All (re_contracts_router.RE_CONTACT_TYPES).
export const RE_TYPES = [
  ['buyer', 'Buyer'], ['seller', 'Seller'], ['past_client', 'Past Client'], ['sphere', 'Sphere'],
  ['referral_partner', 'Referral Partner'], ['investor', 'Investor'],
];
const ALL = 'all';
const LIST_LIMIT = 100;
const SEARCH_DEBOUNCE_MS = 300;

const TIER = {
  on_fire: { bg: '#FEF2F2', color: '#dc2626', icon: <Flame size={12} /> },
  hot: { bg: '#FFEDD5', color: '#c2410c', icon: <Flame size={12} /> },
  warm: { bg: '#FEF9C3', color: '#a16207', icon: null },
  cold: { bg: '#E0F2FE', color: '#0369a1', icon: null },
};
// Score All is a workspace of paid AI calls: owner / admin only. Mirrors the backend
// (permissions.ACTION_KEYS["re.score_all"] -> staff.manage). One contact's Score stays open.
export function canScoreAll(user) {
  return !!(user?.is_super_admin || (user?.permissions || []).includes('staff.manage'));
}
const typeLabel = (k) => (RE_TYPES.find(([key]) => key === k) || [])[1] || (k || '').replace(/_/g, ' ');

export default function HubContactsTab() {
  const auth = useAuth();
  const mayScoreAll = canScoreAll(auth?.user);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [q, setQ] = useState('');
  const [type, setType] = useState(ALL);
  const [showNew, setShowNew] = useState(false);
  const [scoringId, setScoringId] = useState(null);
  const [scoreAllProgress, setScoreAllProgress] = useState(null); // null = idle, else "20 of 61"
  const reqId = useRef(0);

  const load = useCallback((query, kind) => {
    const id = ++reqId.current;
    setLoading(true);
    return uc.list({
      q: query || undefined,
      contact_type: kind === ALL ? RE_TYPES.map(([k]) => k).join(',') : kind,
      limit: LIST_LIMIT,
    }).then((r) => {
      if (id !== reqId.current) return;
      setItems(r.items || []); setTotal(r.total ?? (r.items || []).length); setLoadError('');
    }).catch((e) => {
      if (id !== reqId.current) return;
      setLoadError(errorMessage(e));
    }).finally(() => { if (id === reqId.current) setLoading(false); });
  }, []);

  // Typing waits for a pause; a type change loads straight away (the timer is 0 then).
  const lastQ = useRef('');
  useEffect(() => {
    const wait = q === lastQ.current ? 0 : SEARCH_DEBOUNCE_MS;
    lastQ.current = q;
    const t = setTimeout(() => { load(q.trim(), type); }, wait);
    return () => clearTimeout(t);
  }, [q, type, load]);

  const reload = () => load(q.trim(), type);

  const score = async (cid) => {
    setScoringId(cid);
    try {
      await api.post(`/re/contacts/${cid}/score`);
      toast({ title: 'Lead scored' });
      reload();
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast({ title: 'Scoring failed', description: errorMessage(e) });
    } finally { setScoringId(null); }
  };

  // Score All pages through the server 20 contacts at a time, and reports what really happened.
  const scoreAll = async () => {
    if (scoreAllProgress !== null) return;
    // Each contact is one paid AI call, so ask first.
    if (!(await confirmDialog({
      title: 'Score all real-estate contacts?',
      description: 'Each buyer, seller, past client, sphere, referral partner and investor contact gets one AI score. Mortgage leads are not scored here.',
      confirmText: 'Score contacts',
    }))) return;
    let after = ''; let scored = 0; let failed = 0; let all = 0; let stalled = false;
    setScoreAllProgress('starting');
    try {
      while (after !== null) {
        // eslint-disable-next-line no-await-in-loop
        const { data: r } = await api.post('/re/contacts/score-all', null, { params: after ? { after } : {} });
        scored += r.scored; failed += r.failed; all = r.total; after = r.next_after;
        setScoreAllProgress(`${scored + failed} of ${all}`);
        // A whole batch failing means something is wrong (AI down); stop before the rest.
        if (r.scored === 0 && r.failed > 0) { stalled = true; break; }
      }
      toast({
        title: failed ? `Scored ${scored} of ${all}, ${failed} failed` : `Scored ${scored} of ${all}`,
        description: stalled ? 'Stopped early: a whole batch failed. Try again in a minute.' : undefined,
      });
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) {
        toast({ title: 'Score All stopped', description: `${scored + failed} of ${all || '?'} done. ${errorMessage(e)}` });
      }
    } finally { setScoreAllProgress(null); reload(); }
  };

  const filtered = q.trim() !== '' || type !== ALL;
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="text-sm text-slate-500" data-testid="hub-contacts-count">
          {total} contact{total === 1 ? '' : 's'}
          {total > items.length && ` · showing the first ${items.length}`}
        </div>
        <div className="flex gap-2">
          {mayScoreAll && (
            <button onClick={scoreAll} disabled={scoreAllProgress !== null} data-testid="score-all-btn" className="text-xs font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-60" style={{ background: '#070B5F' }}>
              {scoreAllProgress !== null ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {scoreAllProgress !== null ? `Scoring… ${scoreAllProgress}` : 'Score All Leads'}
            </button>
          )}
          <button onClick={() => setShowNew(true)} data-testid="new-contact-btn" className="text-xs font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: '#6BA82E' }}><Plus size={12} /> New Contact</button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or phone" className="max-w-xs bg-white" data-testid="hub-contacts-search" />
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-44 bg-white" data-testid="hub-contacts-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All real-estate types</SelectItem>
            {RE_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Link to={type === ALL ? '/portal/contacts' : `/portal/contacts?type=${type}`} className="text-xs font-semibold underline text-slate-500 hover:text-slate-700" data-testid="hub-contacts-manage-link">
          Edit, delete and import in Contacts
        </Link>
      </div>

      {loadError && <div className="text-sm text-red-600" role="alert" data-testid="hub-contacts-error">Could not load contacts: {loadError}</div>}
      <div className="grid gap-2">
        {items.map((c) => (
          <ContactCard key={c.contact_id} c={c} onScore={() => score(c.contact_id)} isScoring={scoringId === c.contact_id} />
        ))}
        {!loading && !loadError && items.length === 0 && (
          <div className="bg-white rounded-2xl p-8 border border-dashed border-slate-200 text-center text-sm text-slate-400" data-testid="hub-contacts-empty">
            {filtered ? 'No contacts match. Clear the search or the type filter.' : 'No contacts yet. Add buyers, sellers, sphere, and referral partners.'}
          </div>
        )}
        {loading && <div className="text-sm text-slate-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}
      </div>
      {showNew && <NewContactModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); reload(); }} />}
    </div>
  );
}

function ContactCard({ c, onScore, isScoring }) {
  const tier = c.lead_score_tier || (c.lead_score >= 76 ? 'on_fire' : c.lead_score >= 51 ? 'hot' : c.lead_score >= 26 ? 'warm' : 'cold');
  const tierStyle = TIER[tier] || TIER.warm;
  const name = c.display_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unnamed';
  const phone = primaryPhoneNumber(c);
  const source = c.original_lead_source || c.referral_source;
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm" data-testid={`contact-${c.contact_id}`}>
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/portal/contacts/${c.contact_id}`} className="font-semibold hover:underline" style={{ color: '#070B5F' }} data-testid={`open-contact-${c.contact_id}`}>{name}</Link>
            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded bg-slate-100" style={{ color: '#070B5F' }}>{typeLabel(c.contact_type)}</span>
            {c.lead_score != null && (
              <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded flex items-center gap-1" style={{ background: tierStyle.bg, color: tierStyle.color }}>
                {tierStyle.icon}
                {c.lead_score} · {tier.replace('_', ' ')}
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1">{[primaryEmail(c), phone && formatPhone(phone), source && `via ${source}`].filter(Boolean).join(' · ')}</div>
          <ScoreNotes c={c} />
        </div>
        <button onClick={onScore} disabled={isScoring} data-testid={`score-btn-${c.contact_id}`} className="text-xs font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5 border border-slate-200 hover:bg-slate-50">
          {isScoring ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} style={{ color: '#6BA82E' }} />} Score
        </button>
      </div>
    </div>
  );
}

// What Audrey said about the score: why (bullets; a score from before the bullets has one
// paragraph) and what to do next.
function ScoreNotes({ c }) {
  const reasons = (c.lead_score_reasons || []).filter(Boolean);
  const actions = (c.lead_score_actions || []).filter(Boolean);
  if (!reasons.length && !actions.length && !c.lead_score_reasoning) return null;
  const when = c.lead_scored_at && new Date(c.lead_scored_at);
  const scored = when && !Number.isNaN(when.getTime())
    ? when.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '';
  const bullets = (list) => <ul className="list-disc pl-4 space-y-0.5">{list.map((t, i) => <li key={i}>{t}</li>)}</ul>;
  return (
    <div className="mt-2 text-xs text-slate-600 bg-slate-50 rounded-lg p-2.5 space-y-2" data-testid={`score-notes-${c.contact_id}`}>
      <div>
        <span className="font-semibold" style={{ color: '#070B5F' }}>Audrey AI</span>
        {scored && <span className="text-slate-400"> · scored {scored}</span>}
      </div>
      {(reasons.length > 0 || c.lead_score_reasoning) && (
        <div data-testid={`score-why-${c.contact_id}`}>
          <div className="font-semibold text-slate-700 mb-0.5">Why this score</div>
          {reasons.length > 0 ? bullets(reasons) : <p>{c.lead_score_reasoning}</p>}
        </div>
      )}
      {actions.length > 0 && (
        <div data-testid={`score-next-${c.contact_id}`}>
          <div className="font-semibold text-slate-700 mb-0.5">Next steps</div>
          {bullets(actions)}
        </div>
      )}
    </div>
  );
}

function NewContactModal({ onClose, onCreated }) {
  const [f, setF] = useState({ contact_type: 'buyer', first_name: '', last_name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async () => {
    if (saving) return;
    const email = f.email.trim();
    if (!f.first_name.trim() && !f.last_name.trim() && !email && !f.phone) {
      setError('Give the contact a name, an email or a phone number.');
      return;
    }
    setError(''); setSaving(true);
    try {
      const created = await uc.create({
        contact_type: f.contact_type,
        first_name: f.first_name.trim() || null,
        last_name: f.last_name.trim() || null,
        emails: email ? [{ value: email, is_primary: true }] : [],
        phones: f.phone ? [{ value: f.phone, is_primary: true }] : [],
      });
      toast({ title: 'Contact added', description: created.display_name });
      onCreated();
    } catch (e) {
      setError(errorMessage(e));
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="font-serif text-lg font-bold mb-4" style={{ color: '#070B5F' }}>New Contact</div>
        <div className="space-y-3">
          <div><Label className="text-xs">Type</Label>
            <Select value={f.contact_type} onValueChange={(v) => setF((x) => ({ ...x, contact_type: v }))}>
              <SelectTrigger className="mt-1" data-testid="new-contact-type"><SelectValue /></SelectTrigger>
              <SelectContent>{RE_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">First Name</Label><Input value={f.first_name} onChange={set('first_name')} className="mt-1" data-testid="new-contact-first" /></div>
            <div><Label className="text-xs">Last Name</Label><Input value={f.last_name} onChange={set('last_name')} className="mt-1" data-testid="new-contact-last" /></div>
          </div>
          <div><Label className="text-xs">Email</Label><Input value={f.email} onChange={set('email')} className="mt-1" data-testid="new-contact-email" /></div>
          <div><Label className="text-xs">Phone</Label><PhoneInput value={f.phone} onChange={(d) => setF((x) => ({ ...x, phone: d }))} className="mt-1" /></div>
          {error && <div className="text-xs text-red-600" role="alert" data-testid="new-contact-error">{error}</div>}
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={saving} className="text-sm px-4 py-2 rounded-lg text-white disabled:opacity-60" style={{ background: '#6BA82E' }} data-testid="submit-new-contact">{saving ? 'Adding…' : 'Add'}</button>
        </div>
      </div>
    </div>
  );
}
